import { useRef, useEffect, useState, useCallback } from "react";

import { BACKEND_URL } from "@/utils/apiConfig";
const API_URL = BACKEND_URL;
const WS_URL = API_URL.replace("https://", "wss://").replace("http://", "ws://");

export function useCollaboration({ diagramId, enabled, onNodeMove, onNodesUpdate, onNodeAdd, onNodeRemove, onEdgeAdd, onEdgeRemove, onNodeDataUpdate }) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [myId, setMyId] = useState(null);
  const [myColor, setMyColor] = useState(null);
  const [presence, setPresence] = useState([]);
  const [cursors, setCursors] = useState({});
  const reconnectTimer = useRef(null);

  const fetchWsToken = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/auth/ws-token`, { method: "POST", credentials: "include" });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data.token;
    } catch {
      return null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!enabled || !diagramId) return;
    const token = await fetchWsToken();
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/collab/${diagramId}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case "init":
            setMyId(data.ws_id);
            setMyColor(data.color);
            setPresence(data.presence || []);
            break;

          case "user_joined":
            setPresence(data.presence || []);
            break;

          case "user_left":
            setPresence(data.presence || []);
            setCursors(prev => {
              const next = { ...prev };
              delete next[data.id];
              return next;
            });
            break;

          case "cursor_move":
            setCursors(prev => ({
              ...prev,
              [data.id]: { x: data.position?.x, y: data.position?.y, name: data.name, color: data.color },
            }));
            break;

          case "node_move":
            onNodeMove?.(data.node_id, data.position);
            break;

          case "nodes_update":
            onNodesUpdate?.(data.nodes, data.edges);
            break;

          case "node_add":
            onNodeAdd?.(data.node);
            break;

          case "node_remove":
            onNodeRemove?.(data.node_id);
            break;

          case "edge_add":
            onEdgeAdd?.(data.edge);
            break;

          case "edge_remove":
            onEdgeRemove?.(data.edge_id);
            break;

          case "node_data_update":
            onNodeDataUpdate?.(data.node_id, data.data);
            break;

          default:
            break;
        }
      } catch {
        // Ignore parse errors
      }
    };

    ws.onclose = (e) => {
      setConnected(false);
      wsRef.current = null;
      // Reconnect unless deliberately closed or auth error
      if (e.code < 4000 && enabled) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      // Will trigger onclose
    };
  }, [enabled, diagramId, fetchWsToken, onNodeMove, onNodesUpdate, onNodeAdd, onNodeRemove, onEdgeAdd, onEdgeRemove, onNodeDataUpdate]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendCursorMove = useCallback((position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "cursor_move", position }));
    }
  }, []);

  const sendNodeMove = useCallback((nodeId, position) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "node_move", node_id: nodeId, position }));
    }
  }, []);

  const sendNodesUpdate = useCallback((nodes, edges) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "nodes_update", nodes, edges }));
    }
  }, []);

  const sendNodeAdd = useCallback((node) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "node_add", node }));
    }
  }, []);

  const sendNodeRemove = useCallback((nodeId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "node_remove", node_id: nodeId }));
    }
  }, []);

  const sendEdgeAdd = useCallback((edge) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "edge_add", edge }));
    }
  }, []);

  const sendEdgeRemove = useCallback((edgeId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "edge_remove", edge_id: edgeId }));
    }
  }, []);

  const sendNodeDataUpdate = useCallback((nodeId, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "node_data_update", node_id: nodeId, data }));
    }
  }, []);

  return {
    connected,
    myId,
    myColor,
    presence,
    cursors,
    sendCursorMove,
    sendNodeMove,
    sendNodesUpdate,
    sendNodeAdd,
    sendNodeRemove,
    sendEdgeAdd,
    sendEdgeRemove,
    sendNodeDataUpdate,
  };
}
